<template>
  <a-input
    :id="googleAutocompleteInputFieldId"
    ref="autocompleteInput"
    v-model:value="autocompleteText"
    data-tn="google-autocomplete"
    size="large"
    type="text"
    :class="classname"
    :placeholder="placeholder"
    :disabled="disabled"
    @focus="onFocus"
    @blur="onBlur"
    @change="onChange"
    @keypress="onKeyPress"
    @keyup="onKeyUp"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import {
  BASIC_DATA_FIELDS,
  ADDRESS_COMPONENTS,
  CITIES_TYPE,
  REGIONS_TYPE,
} from '../constants/google-autocomplete';

type AutocompleteInstance = {
  setFields: (fields?: string[]) => void;
  addListener: (eventName: string, handler: () => void) => void;
  getPlace: () => PlaceResult | undefined;
  setComponentRestrictions: (restrictions: { country: string | string[] }) => void;
  setBounds: (bounds: unknown) => void;
};

type GeocoderInstance = {
  geocode: (
    request: { location: { lat: number; lng: number } },
    callback: (results: PlaceResult[], status: string) => void,
  ) => void;
};

type CircleInstance = {
  getBounds: () => unknown;
};

type GoogleMaps = {
  maps?: {
    places?: {
      Autocomplete: new (
        input: HTMLInputElement,
        options: Record<string, unknown>,
      ) => AutocompleteInstance;
    };
    Geocoder: new () => GeocoderInstance;
    Circle: new (options: {
      center: { lat: number; lng: number };
      radius?: number;
    }) => CircleInstance;
  };
};

type GeoPosition = {
  coords: { latitude: number; longitude: number; accuracy?: number };
};

type PlaceResult = {
  address_components?: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;
  geometry?: {
    location: { lat: () => number; lng: () => number };
  };
  formatted_address?: string;
  types?: string[];
};

const props = withDefaults(
  defineProps<{
    id: string;
    modelValue?: string;
    classname?: string;
    placeholder?: string;
    disabled?: boolean;
    types?: string[];
    fields?: string[];
    country?: string | string[] | null;
    enableGeolocation?: boolean;
    geolocationOptions?: Record<string, unknown> | null;
  }>(),
  {
    modelValue: '',
    classname: '',
    placeholder: '',
    disabled: false,
    /**
     * What we want is actually types: ['address', 'establishment'].
     * But the Legacy Google API doesn't allow filtering by the combination of 'address' + 'establishment' types.
     * Using 'geocode' type as compromise, but it is very wide, including region or city in the result.
     * https://developers.google.com/maps/documentation/javascript/legacy/supported_types#table3
     */
    types: () => ['geocode', 'establishment'],
    fields: () => BASIC_DATA_FIELDS,
    country: null,
    enableGeolocation: false,
    geolocationOptions: null,
  },
);

const googleAutocompleteInputFieldId = computed(() => `google-autocomplete-${props.id}`);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'placechanged', formatted: Record<string, unknown>, place: PlaceResult, id: string): void;
  (e: 'no-results-found', place: PlaceResult, id: string): void;
  (e: 'focus'): void;
  (e: 'blur'): void;
  (e: 'change', value: string): void;
  (e: 'keypress', event: KeyboardEvent): void;
  (e: 'keyup', event: KeyboardEvent): void;
  (e: 'error', message: string, error?: unknown): void;
}>();

const autocompleteInput = ref<HTMLInputElement | null>(null);
const autocompleteText = ref(props.modelValue);
const autocomplete = ref<AutocompleteInstance | null>(null);
const geolocation = ref<{
  geocoder: GeocoderInstance | null;
  loc: { lat: number; lng: number } | null;
  position: GeoPosition | null;
}>({
  geocoder: null,
  loc: null,
  position: null,
});

const getGoogleMaps = () => (window as { google?: GoogleMaps }).google;

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== autocompleteText.value) {
      autocompleteText.value = newValue ?? '';
    }
  },
);

watch(autocompleteText, (newValue) => {
  emit('update:modelValue', newValue);
});

watch(
  () => props.country,
  () => {
    if (!autocomplete.value) return;
    autocomplete.value.setComponentRestrictions({
      country: props.country === null ? [] : props.country,
    });
  },
);

onMounted(() => {
  const googleMaps = getGoogleMaps();
  if (!googleMaps?.maps?.places?.Autocomplete) {
    emit('error', 'Google Maps Places API is not available');
    return;
  }

  const options: Record<string, unknown> = {};
  if (props.types && props.types.length > 0) {
    options.types = props.types;
  }
  if (props.country) {
    options.componentRestrictions = { country: props.country };
  }

  autocomplete.value = new googleMaps.maps.places.Autocomplete(
    document.getElementById(googleAutocompleteInputFieldId.value) as HTMLInputElement,
    options,
  );
  autocomplete.value.setFields(props.fields);
  autocomplete.value.addListener('place_changed', onPlaceChanged);
});

const onPlaceChanged = () => {
  const place = autocomplete.value?.getPlace() as PlaceResult | undefined;
  if (!place || !place.geometry) {
    emit('no-results-found', place || {}, props.id);
    return;
  }
  if (place.address_components) {
    emit('placechanged', formatResult(place), place, props.id);
    autocompleteText.value = (
      document.getElementById(googleAutocompleteInputFieldId.value) as HTMLInputElement
    ).value;
    onChange();
  }
};

const onFocus = () => {
  biasAutocompleteLocation();
  emit('focus');
};

const onBlur = () => {
  emit('blur');
};

const onChange = () => {
  emit('change', autocompleteText.value);
};

const onKeyPress = (event: KeyboardEvent) => {
  emit('keypress', event);
};

const onKeyUp = (event: KeyboardEvent) => {
  emit('keyup', event);
};

const updateCoordinates = (value: { lat: number; lng: number } | null) => {
  if (!value || !(value.lat || value.lng)) return;
  const googleMaps = getGoogleMaps();
  if (!geolocation.value.geocoder) {
    if (!googleMaps?.maps?.Geocoder) return;
    geolocation.value.geocoder = new googleMaps.maps.Geocoder();
  }
  geolocation.value.geocoder.geocode(
    { location: value },
    (results: PlaceResult[], status: string) => {
      if (status === 'OK') {
        const filtered = filterGeocodeResultTypes(results);
        if (filtered[0]) {
          emit('placechanged', formatResult(filtered[0]), filtered[0], props.id);
          autocompleteText.value = filtered[0].formatted_address || '';
        } else {
          emit('error', 'no result for provided coordinates');
        }
      } else {
        emit('error', 'error getting address from coords');
      }
    },
  );
};

const updateGeolocation = (callback?: (geo: { lat: number; lng: number }) => void) => {
  if (!navigator.geolocation) return;
  const options = props.geolocationOptions ? { ...props.geolocationOptions } : undefined;
  navigator.geolocation.getCurrentPosition(
    (position: GeoPosition) => {
      const geo = { lat: position.coords.latitude, lng: position.coords.longitude };
      geolocation.value.loc = geo;
      geolocation.value.position = position;
      if (callback) callback(geo);
    },
    (err) => {
      emit('error', 'Cannot get Coordinates from navigator', err);
    },
    options,
  );
};

const geolocate = () => {
  updateGeolocation((geo) => updateCoordinates(geo));
};

const biasAutocompleteLocation = () => {
  const autocompleteInstance = autocomplete.value;
  if (!props.enableGeolocation || !autocompleteInstance) return;
  updateGeolocation((geo) => {
    const googleMaps = getGoogleMaps();
    if (!googleMaps?.maps?.Circle) return;
    const circle = new googleMaps.maps.Circle({
      center: geo,
      radius: geolocation.value.position?.coords.accuracy,
    });
    autocompleteInstance.setBounds(circle.getBounds());
  });
};

const formatResult = (place: PlaceResult) => {
  const returnData: Record<string, unknown> = {};
  for (let i = 0; i < (place.address_components || []).length; i += 1) {
    const addressType = place.address_components?.[i]?.types?.[0];
    if (addressType && ADDRESS_COMPONENTS[addressType]) {
      returnData[addressType] =
        place.address_components?.[i]?.[ADDRESS_COMPONENTS[addressType]] ?? '';
    }
  }
  returnData.latitude = place.geometry?.location.lat();
  returnData.longitude = place.geometry?.location.lng();
  return returnData;
};

const filterGeocodeResultTypes = (results: PlaceResult[]) => {
  if (!results || !props.types) return results;
  let types = props.types;
  if (types.includes('(cities)')) types = types.concat(CITIES_TYPE);
  if (types.includes('(regions)')) types = types.concat(REGIONS_TYPE);
  return results.filter((result) => (result.types || []).some((t) => types.includes(t)));
};

defineExpose({ geolocate });
</script>
